import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Briefcase, Phone, Mail, Home, Power, Pencil, Trash2, Loader2, StickyNote, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { useCustomFields, CustomFieldsView, CustomFieldsEdit, CustomFieldManager } from "@/components/CustomFields";
import { confirm } from "@/components/ConfirmDialog";

export default function ClientProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ name: "", contact: "", email: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [cfValues, setCfValues] = useState({});
  const [cfMgr, setCfMgr] = useState(false);
  const { defs: cfDefs, reload: reloadDefs } = useCustomFields("client");

  const load = useCallback(() => {
    client.get(`/clients/${id}`).then((r) => {
      setC(r.data);
      setEdit({ name: r.data.name || "", contact: r.data.contact || "", email: r.data.email || "", notes: r.data.notes || "" });
      setCfValues(r.data.customFields || {});
    }).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await client.patch(`/clients/${id}`, edit);
      if (cfDefs.length) await client.put(`/custom-values/client/${id}`, { values: cfValues });
      toast.success(t("common.saved", "Saved")); setEditing(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const lifecycle = async (action) => {
    const verb = action === "delete" ? t("cli.delConfirm", "Delete this client? Only possible if no properties/jobs are linked.") : t(`cli.${action}Confirm`, `${action} this client?`);
    if (!(await confirm({ title: t(`cli.${action}`, action), description: verb, confirmText: t(`cli.${action}`, action), destructive: action !== "reactivate" }))) return;
    try {
      if (action === "delete") { await client.delete(`/clients/${id}`); toast.success(t("common.saved", "Done")); nav("/clients"); return; }
      await client.post(`/clients/${id}/${action}`); toast.success(t("common.saved", "Done")); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!c) return <div className="p-8 text-slate-400" data-testid="client-profile-loading">Loading…</div>;
  const inactive = c.status === "deactivated";

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      <button onClick={() => nav("/clients")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand" data-testid="client-back">
        <ArrowLeft className="h-4 w-4" />{t("nav.clients", "Clients")}
      </button>

      <Card className="p-6 border-slate-200">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-brand/10 text-brand flex items-center justify-center"><Briefcase className="h-6 w-6" /></div>
            <div>
              <h2 className="font-display text-2xl font-bold text-brand-ink flex items-center gap-2">{c.name}{inactive && <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("staff.inactive", "Inactive")}</Badge>}</h2>
              {c.contact && <p className="text-sm text-slate-500 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{c.contact}</p>}
              <div className="flex gap-2 mt-2">
                <Badge variant="outline" className="bg-slate-50"><Home className="h-3 w-3 mr-1" />{c.propertyCount ?? 0} {t("cli.properties", "Properties")}</Badge>
                <Badge variant="outline" className="bg-slate-50">{c.jobCount ?? 0} {t("prop.jobs", "jobs")}</Badge>
              </div>
            </div>
          </div>
          {can("client.manage") && (
            <div className="flex flex-wrap gap-2" data-testid="client-actions">
              {!editing && <Button size="sm" variant="outline" data-testid="client-edit-btn" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5 mr-1.5" />{t("common.edit", "Edit")}</Button>}
              <Button size="sm" variant="outline" data-testid="client-manage-fields" onClick={() => setCfMgr(true)}><Settings2 className="h-3.5 w-3.5 mr-1.5" />{t("cf.manage", "Custom fields")}</Button>
              {inactive
                ? <Button size="sm" variant="outline" data-testid="client-reactivate" onClick={() => lifecycle("reactivate")}><Power className="h-3.5 w-3.5 mr-1.5" />{t("cli.reactivate", "Reactivate")}</Button>
                : <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" data-testid="client-deactivate" onClick={() => lifecycle("deactivate")}><Power className="h-3.5 w-3.5 mr-1.5" />{t("cli.deactivate", "Deactivate")}</Button>}
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" data-testid="client-delete" onClick={() => lifecycle("delete")}><Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("common.delete", "Delete")}</Button>
            </div>
          )}
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="cp-tab-overview">{t("cli.tab.overview", "Overview")}</TabsTrigger>
          <TabsTrigger value="properties" data-testid="cp-tab-properties">{t("nav.properties", "Properties")} ({(c.relatedProperties || []).length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="cp-tab-notes">{t("common.notes", "Notes")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-4">
          <Card className="p-5 border-slate-200 max-w-2xl space-y-3">
            {editing ? (
              <>
                <div><Label>{t("common.name", "Name")}</Label><Input data-testid="client-edit-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("cli.contact", "Contact")}</Label><Input data-testid="client-edit-contact" value={edit.contact} onChange={(e) => setEdit({ ...edit, contact: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("cli.email", "Email (for job updates)")}</Label><Input data-testid="client-edit-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="name@company.com" className="mt-1.5" /></div>
                <div><Label>{t("common.notes", "Notes")}</Label><Textarea data-testid="client-edit-notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className="mt-1.5" /></div>
                <CustomFieldsEdit defs={cfDefs} values={cfValues} onChange={setCfValues} />
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => { setEditing(false); load(); }}>{t("common.cancel", "Cancel")}</Button>
                  <Button data-testid="client-edit-save" onClick={save} disabled={busy || !edit.name} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save", "Save")}</Button>
                </div>
              </>
            ) : (
              <>
                <Row icon={Phone} label={t("cli.contact", "Contact")} value={c.contact || "—"} />
                <Row icon={Mail} label={t("cli.email", "Email")} value={c.email || "—"} />
                <Row icon={Home} label={t("cli.properties", "Properties")} value={String(c.propertyCount ?? 0)} />
                <Row icon={Power} label={t("common.status", "Status")} value={inactive ? t("staff.inactive", "Inactive") : t("st.active", "Active")} />
                <CustomFieldsView defs={cfDefs} values={c.customFields} />
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="properties" className="pt-4">
          <Card className="border-slate-200 overflow-hidden">
            {(c.relatedProperties || []).length === 0 ? <p className="p-6 text-sm text-slate-400">{t("cli.noProps", "No properties for this client.")}</p> : (
              <div className="divide-y divide-slate-50">
                {c.relatedProperties.map((p) => (
                  <button key={p.id} data-testid={`cp-property-${p.id}`} onClick={() => nav(`/properties/${p.id}`)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition">
                    <span className="text-sm text-slate-800 flex items-center gap-2"><Home className="h-3.5 w-3.5 text-slate-400" />{p.address}{p.status === "deactivated" && <Badge variant="outline" className="bg-slate-100 text-slate-500 text-[10px]">{t("staff.inactive", "Inactive")}</Badge>}</span>
                    <Badge variant="outline" className="bg-slate-50">{p.jobCount ?? 0} {t("prop.jobs", "jobs")}</Badge>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="notes" className="pt-4">
          <Card className="p-5 border-slate-200 max-w-2xl">
            <div className="flex items-start gap-2 text-sm text-slate-600"><StickyNote className="h-4 w-4 mt-0.5 text-slate-400" /><p className="whitespace-pre-wrap">{c.notes || t("common.none", "No notes.")}</p></div>
          </Card>
        </TabsContent>
      </Tabs>

      <CustomFieldManager entity="client" open={cfMgr} onOpenChange={setCfMgr} onChanged={reloadDefs} />
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
    <Icon className="h-4 w-4 text-slate-400" /><span className="text-xs uppercase tracking-wide text-slate-400 w-28">{label}</span><span className="text-sm text-slate-700">{value}</span>
  </div>
);
